import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronLeft, ChevronRight, Search, AlertTriangle, Eye, X, Flag } from 'lucide-react';
import ThemeChanger from '../Components/ThemeChanger';
import api from '../api';

export default function AdminReport() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [reportedQuestions, setReportedQuestions] = useState([]);
  const [allReportedQuestions, setAllReportedQuestions] = useState([]); // Store all fetched questions for filtering
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    itemsPerPage: 10
  });
  const [searchTerm, setSearchTerm] = useState('');
  const [detailedQuestion, setDetailedQuestion] = useState(null);
  const [topicFilter, setTopicFilter] = useState('all');
  const [topics, setTopics] = useState([]);
  const [reasonFilter, setReasonFilter] = useState('all');

  const reasons = [
    { value: 'all', label: 'All Reasons' },
    { value: 'wrong_answer', label: 'Wrong Answer' },
    { value: 'incomplete_question', label: 'Incomplete Question' }
  ];

  // Fetch all reported questions (only use pagination for API)
  useEffect(() => {
    const fetchReportedQuestions = async () => {
      setLoading(true);
      try {
        const params = new URLSearchParams({
          page: pagination.currentPage,
          limit: pagination.itemsPerPage
        });

        // Only include search in API params, filtering will be done client-side
        if (searchTerm) params.append('search', searchTerm);

        const response = await api.get(`/admin/get-reported-questions?${params}`);

        if (response.data.statusCode === 200) {
          const fetchedQuestions = response.data.data || [];
          setReportedQuestions(fetchedQuestions);
          setAllReportedQuestions(fetchedQuestions); // Store all questions for filtering
          setPagination(response.data.pagination);
          applyFilters(fetchedQuestions); // Apply filters to the fetched data
        } else {
          throw new Error(response.data.message || 'Failed to fetch reported questions');
        }
      } catch (err) {
        console.error('Error fetching reported questions:', err);
        setError(err.message || 'Failed to load reported questions');
      } finally {
        setLoading(false);
      }
    };

    fetchReportedQuestions();
  }, [pagination.currentPage, pagination.itemsPerPage, searchTerm]); // Only depend on pagination and search

  // Apply filters client-side
  const applyFilters = (questions) => {
    if (!questions || !questions.length) return;

    let filteredQuestions = [...questions];

    // Apply topic filter - Fix: use correct property names from the data
    if (topicFilter !== 'all') {
      filteredQuestions = filteredQuestions.filter(question => {
        // Check if the topic matches by ID
        const topicMatches = question.topicId === topicFilter;

        // Some APIs might return name instead of ID, so check both possibilities
        const topicNameMatches = topics.find(t => t._id === topicFilter)?.name === question.topicName;

        return topicMatches || topicNameMatches;
      });
    }

    // Apply reason filter
    if (reasonFilter !== 'all') {
      filteredQuestions = filteredQuestions.filter(question =>
        question.reportReason === reasonFilter
      );
    }

    setReportedQuestions(filteredQuestions);

    // Update local pagination values (not API call)
    const totalFilteredItems = filteredQuestions.length;
    const totalFilteredPages = Math.ceil(totalFilteredItems / pagination.itemsPerPage) || 1;

    setPagination(prev => ({
      ...prev,
      totalItems: totalFilteredItems,
      totalPages: totalFilteredPages,
      currentPage: Math.min(prev.currentPage, totalFilteredPages)
    }));
  };

  // Re-apply filters when filters change
  useEffect(() => {
    applyFilters(allReportedQuestions);
  }, [topicFilter, reasonFilter]);

  // Fetch topics for filter dropdown
  useEffect(() => {
    const fetchTopics = async () => {
      try {
        const response = await api.get('/admin/get-all-topics');
        if (response.data.statusCode === 200) {
          setTopics(response.data.data || []);
        }
      } catch (err) {
        console.error('Error fetching topics:', err);
      }
    };

    fetchTopics();
  }, []);

  // Format date for display
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return new Intl.DateTimeFormat('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    }).format(date);
  };

  // Handle search - still uses API
  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
    setPagination(prev => ({ ...prev, currentPage: 1 })); // Reset to first page on search
  };

  // Pagination controls
  const changePage = (newPage) => {
    setPagination(prev => ({ ...prev, currentPage: newPage }));
  };

  // Handle filter changes
  const handleTopicFilterChange = (e) => {
    setTopicFilter(e.target.value);
    setPagination(prev => ({ ...prev, currentPage: 1 })); // Reset to first page on filter change
  };

  const handleReasonFilterChange = (e) => {
    setReasonFilter(e.target.value);
    setPagination(prev => ({ ...prev, currentPage: 1 })); // Reset to first page on filter change  
  };

  // View detailed question
  const viewDetailedQuestion = (question) => {
    setDetailedQuestion(question);
  };

  return (
    <div className="min-h-screen bg-base-200 p-4">
      {/* Header with navigation and action buttons */}
      <div className="fixed top-0 left-0 right-0 z-10 px-4 py-3 bg-base-100 shadow-md">
        <div className="w-full max-w-7xl mx-auto flex justify-between items-center">
          <div className="flex items-center gap-2">
            <button
              className="btn btn-info btn-outline btn-sm"
              onClick={() => navigate('/')}
            >
              Home
            </button>
            <button
              className="btn btn-warning btn-outline btn-sm"
              onClick={() => navigate('/admin')}
            >
              Dashboard
            </button>
          </div>
          <div className="flex items-center gap-3">
            <button
              className="btn btn-primary btn-sm"
              onClick={() => navigate('/admin/topics')}
            >
              Topics Management
            </button>
            <ThemeChanger />
          </div>
        </div>
      </div>

      {/* Main content with padding for fixed header */}
      <div className="pt-16 max-w-7xl mx-auto">
        <div className="flex flex-col md:flex-row justify-between items-center mb-6">
          <h1 className="text-2xl font-bold">Reported Questions</h1>
          <div className="badge badge-error gap-1 text-sm">
            <AlertTriangle size={14} />
            {pagination.totalItems} Reports
          </div>
        </div>

        {/* Filter and search controls */}
        <div className="flex flex-col md:flex-row justify-between gap-4 mb-6">
          <div className="flex flex-col sm:flex-row gap-3 w-full md:w-auto">
            <select
              className="select select-bordered w-full sm:w-48"
              value={topicFilter}
              onChange={handleTopicFilterChange}
              disabled={loading}
            >
              <option value="all">All Topics</option>
              {topics.map((topic) => (
                <option key={topic._id} value={topic._id}>
                  {topic.name}
                </option>
              ))}
            </select>

            <select
              className="select select-bordered w-full sm:w-48"
              value={reasonFilter}
              onChange={handleReasonFilterChange}
              disabled={loading}
            >
              {reasons.map((reason) => (
                <option key={reason.value} value={reason.value}>
                  {reason.label}
                </option>
              ))}
            </select>
          </div>

          {/* Search still uses API filtering */}
          <div className="relative w-full md:w-64">
            <div className="absolute inset-y-0 left-3 flex items-center pointer-events-none">
              <Search size={18} className="text-base-content/50" />
            </div>
            <input
              type="text"
              placeholder="Search by question or user..."
              className="input input-bordered pl-10 w-full"
              value={searchTerm}
              onChange={handleSearch}
            />
          </div>
        </div>

        {/* Loading state */}
        {loading && (
          <div className="flex justify-center items-center py-20">
            <div className="flex flex-col items-center gap-4">
              <span className="loading loading-spinner loading-lg text-primary"></span>
              <p className="text-base-content/70">Loading reported questions...</p>
            </div>
          </div>
        )}

        {/* Error state */}
        {error && !loading && (
          <div className="alert alert-error mb-6">
            <div>
              <span>{error}</span>
            </div>
          </div>
        )}

        {/* Table View */}
        {!loading && !error && (
          <div className="bg-base-100 rounded-lg shadow overflow-hidden">
            <div className="overflow-x-auto">
              <table className="table table-zebra w-full">
                <thead>
                  <tr>
                    <th>Question</th>
                    <th>Topic</th>
                    <th>Reason</th>
                    <th>Reported By</th>
                    <th>Date</th>
                    <th className="w-24 text-center">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {reportedQuestions.length > 0 ? (
                    reportedQuestions.map((report) => (
                      <tr key={report._id} className="hover">
                        <td className="max-w-xs truncate">
                          {report.question.length > 60
                            ? `${report.question.substring(0, 60)}...`
                            : report.question}
                        </td>
                        <td>
                          <div className="flex flex-col">
                            <span className="font-medium">{report.topicName}</span>
                            <span className="text-xs opacity-70">{report.subTopicName}</span>
                          </div>
                        </td>
                        <td>
                          <span className="badge badge-outline badge-error">
                            {report.reportReason}
                          </span>
                        </td>
                        <td>
                          <div className="flex flex-col">
                            <span>{report.userName}</span>
                            <span className="text-xs opacity-70">{report.userEmail}</span>
                          </div>
                        </td>
                        <td>{formatDate(report.reportDate)}</td>
                        <td className="text-center">
                          <button
                            className="btn btn-sm btn-info btn-outline"
                            onClick={() => viewDetailedQuestion(report)}
                          >
                            <Eye size={16} />
                          </button>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="6" className="text-center py-8">
                        {searchTerm || topicFilter !== 'all' || reasonFilter !== 'all'
                          ? 'No results match your search criteria.'
                          : 'No reported questions found.'}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Pagination */}
        {!loading && !error && pagination.totalPages > 1 && (
          <div className="flex justify-center mt-6">
            <div className="join">
              <button
                className="join-item btn"
                onClick={() => changePage(pagination.currentPage - 1)}
                disabled={pagination.currentPage === 1}
              >
                <ChevronLeft />
              </button>

              {[...Array(pagination.totalPages)].map((_, index) => {
                // Show limited page numbers with ellipsis for better UI
                const pageNum = index + 1;
                if (
                  pageNum === 1 ||
                  pageNum === pagination.totalPages ||
                  (pageNum >= pagination.currentPage - 1 && pageNum <= pagination.currentPage + 1)
                ) {
                  return (
                    <button
                      key={index}
                      className={`join-item btn ${pagination.currentPage === pageNum ? 'btn-active' : ''}`}
                      onClick={() => changePage(pageNum)}
                    >
                      {pageNum}
                    </button>
                  );
                } else if (
                  (pageNum === 2 && pagination.currentPage > 3) ||
                  (pageNum === pagination.totalPages - 1 && pagination.currentPage < pagination.totalPages - 2)
                ) {
                  return (
                    <button key={index} className="join-item btn btn-disabled">
                      ...
                    </button>
                  );
                } else {
                  return null;
                }
              })}

              <button
                className="join-item btn"
                onClick={() => changePage(pagination.currentPage + 1)}
                disabled={pagination.currentPage === pagination.totalPages}
              >
                <ChevronRight />
              </button>
            </div>
          </div>
        )}

        {/* Table info */}
        {!loading && !error && reportedQuestions.length > 0 && (
          <div className="text-center mt-3 text-sm text-base-content/70">
            Showing {(pagination.currentPage - 1) * pagination.itemsPerPage + 1} to{' '}
            {Math.min(pagination.currentPage * pagination.itemsPerPage, pagination.totalItems)} of{' '}
            {pagination.totalItems} reports
          </div>
        )}
      </div>

      {/* Detailed Question Modal */}
      {detailedQuestion && (
        <div className="fixed inset-0 bg-black/50 z-[100] flex items-center justify-center p-4">
          <div className="bg-base-100 rounded-lg shadow-xl max-w-3xl w-full overflow-auto max-h-[90vh]">
            <div className="sticky top-0 bg-base-100 p-4 border-b flex justify-between items-center">
              <h3 className="font-bold text-xl flex items-center gap-2">
                <Flag className="text-error" size={20} />
                Reported Question
              </h3>
              <button
                className="btn btn-sm btn-circle"
                onClick={() => setDetailedQuestion(null)}
              >
                <X size={16} />
              </button>
            </div>

            <div className="p-6">
              {/* Report details */}
              <div className="mb-6">
                <div className="flex justify-between items-start mb-4 flex-wrap gap-3">
                  <div>
                    <h4 className="font-semibold text-lg">Report Information</h4>
                    <p className="text-sm text-base-content/70">
                      Reported on {formatDate(detailedQuestion.reportDate)}
                    </p>
                  </div>
                  <div className="badge badge-error badge-lg">
                    {detailedQuestion.reportReason}
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                  <div className="bg-base-200 p-4 rounded-lg">
                    <h5 className="font-medium mb-1">Reported By</h5>
                    <p>{detailedQuestion.userName}</p>
                    <p className="text-sm opacity-70">{detailedQuestion.userEmail}</p>
                  </div>
                  <div className="bg-base-200 p-4 rounded-lg">
                    <h5 className="font-medium mb-1">Topic Information</h5>
                    <p>{detailedQuestion.topicName}</p>
                    <p className="text-sm opacity-70">{detailedQuestion.subTopicName}</p>
                  </div>
                </div>

                {detailedQuestion.additionalInfo && (
                  <div className="bg-base-200 p-4 rounded-lg">
                    <h5 className="font-medium mb-1">Additional Information</h5>
                    <p className="whitespace-pre-wrap">{detailedQuestion.additionalInfo}</p>
                  </div>
                )}
              </div>

              <div className="divider"></div>

              {/* Question details */}
              <div>
                <h4 className="font-semibold text-lg mb-4">Question Details</h4>
                <div className="bg-base-200 p-4 rounded-lg mb-4">
                  <p className="font-medium">{detailedQuestion.question}</p>
                </div>

                <h5 className="font-medium mb-2">Options:</h5>
                <div className="flex flex-col gap-2 mb-4">
                  {detailedQuestion.options.map((option, idx) => (
                    <div
                      key={idx}
                      className={`p-3 border rounded-lg ${idx.toString() === detailedQuestion.answer.toString()
                          ? 'bg-success/10 border-success'
                          : 'bg-base-100'
                        }`}
                    >
                      <div className="flex items-center gap-2">
                        {idx.toString() === detailedQuestion.answer.toString() && (
                          <span className="badge badge-success">Correct</span>
                        )}
                        <span>{option}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="flex justify-end mt-6 gap-3">
                <button
                  className="btn btn-outline"
                  onClick={() => setDetailedQuestion(null)}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}